import React, { useCallback, useEffect, useRef } from 'react';
import { Mic } from 'lucide-react';
import { useVoiceDictation } from './useVoiceDictation';

export interface AutoGrowTextAreaProps
  extends Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, 'value' | 'onChange'> {
  /** Controlled value. */
  value: string;
  /** Called with the next value on typing or voice dictation. */
  onValueChange: (value: string) => void;
  /** Show the voice-dictation mic when the browser supports it (default true). */
  enableVoice?: boolean;
  /** Minimum rendered height in px (default 72). */
  minHeight?: number;
}

/**
 * A textarea that grows with its content (no fixed row clip) and offers
 * optional voice dictation via the Web Speech API. Framework-light: depends
 * only on React and lucide-react, so it can live in a shared package consumed
 * by every field PWA. AI polish is intentionally NOT built in — that requires
 * an app-specific Cloud Function, so richer editors are injected by the host
 * app via the NoteCard `renderEditor` slot.
 */
export function AutoGrowTextArea({
  value,
  onValueChange,
  enableVoice = true,
  minHeight = 72,
  className,
  ...textareaProps
}: AutoGrowTextAreaProps): React.ReactElement {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  // Track the live value so a voice session (whose handler is created once)
  // always appends to the latest text rather than a stale closure snapshot.
  const valueRef = useRef(value);
  valueRef.current = value;

  const grow = useCallback(
    (el: HTMLTextAreaElement | null) => {
      if (!el) return;
      el.style.height = 'auto';
      el.style.height = `${Math.max(el.scrollHeight, minHeight)}px`;
    },
    [minHeight]
  );

  // Re-fit whenever the value changes (typing, dictation, or external reset).
  useEffect(() => {
    grow(textareaRef.current);
  }, [value, grow]);

  const handleTranscript = useCallback(
    (transcript: string) => {
      const current = valueRef.current;
      const next = current ? `${current.replace(/\s+$/, '')} ${transcript}` : transcript;
      onValueChange(next);
    },
    [onValueChange]
  );

  const voice = useVoiceDictation({ onResult: handleTranscript });
  const showMic = enableVoice && voice.supported && !textareaProps.disabled;

  return (
    <div className="relative">
      <textarea
        ref={textareaRef}
        value={value}
        onChange={(e) => {
          onValueChange(e.target.value);
          grow(e.target);
        }}
        className={`w-full resize-none overflow-hidden rounded-xl border border-slate-300 px-2.5 py-2 text-sm shadow-sm focus:border-[#007ACC] focus:outline-none ${
          showMic ? 'pr-11' : ''
        } ${className ?? ''}`}
        {...textareaProps}
      />
      {showMic && (
        <button
          type="button"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => (voice.listening ? voice.stop() : voice.start())}
          aria-label={voice.listening ? 'Stop dictation' : 'Tap mic to dictate'}
          aria-pressed={voice.listening}
          className={`absolute right-1.5 top-1.5 flex h-9 w-9 items-center justify-center rounded-lg transition-colors ${
            voice.listening
              ? 'bg-red-50 text-red-600'
              : 'text-slate-400 hover:bg-slate-100 hover:text-slate-700'
          }`}
        >
          <Mic size={16} className={voice.listening ? 'animate-pulse' : undefined} />
        </button>
      )}
    </div>
  );
}
